/**
 * webamp-controller.js  — Tight integration layer
 *
 * Intercepts Webamp's internal audio element and redirects all
 * playback commands to whatever bridge (Apple Music or Spotify) is
 * currently active.  The bridge is swappable at runtime via switchBridge().
 *
 * Key discoveries from live debugging:
 *
 *  - Webamp's audio element lives at webamp.media._source._audio.
 *  - Redux store is at webamp.store.
 *    state.playlist.currentTrack  — integer track ID currently selected
 *    state.playlist.trackOrder    — ordered array of track IDs [1,2,3,…]
 *    state.media.volume           — 0-100
 *
 *  - 'durationchange' on the audio element triggers Webamp's "loaded" state.
 *
 *  - We use ctrl.mk (not a captured mk variable) for every bridge call so
 *    switchBridge() automatically takes effect without re-patching the element.
 *
 *  - MusicKit v3 setQueue() DOUBLE trackchange problem (see original comments):
 *    Fixed with _webampInitiatedTarget index guard.
 */

export class WebampController {
  constructor(webamp, bridge) {
    this.webamp = webamp;
    this.mk     = bridge;   // The active bridge (MusicKitBridge or SpotifyBridge)

    this._trackIds          = [];
    this._currentPlaylistId = null;
    this._audioEl           = null;
    this._unsubscribeStore  = null;
    this._unsubscribeTrack  = null;

    // Queue coordination flags consumed by audio.play()
    this._queueNeedsSetup  = false;
    this.pendingStartIndex = null;
    this._pendingSkip      = null;   // 'next' | 'prev' | null

    // Set true when the bridge auto-advances so seekToTime(0) from source.play()
    // doesn't reset the already-playing next track.
    this._mkAutoAdvanced = false;

    // The queue index we intentionally navigated to.
    // While non-null, ALL trackchange events are suppressed (they're ours).
    // Cleared when the bridge reports nowPlayingItemIndex === this value.
    this._webampInitiatedTarget = null;

    // Bridge event handlers — stored so they can be removed on switchBridge()
    this._bridgeHandlers = null;
    this._prevTrackIndex = -1;   // replaces closure variable for switchBridge compat
    this._setTime        = null; // set by _patchAudioElement
  }

  // ─────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────

  init() {
    this._findAndPatchAudio();
    this._subscribeStoreVolume();
    this._subscribeCurrentTrackChanges();
  }

  setPlaylist(tracks, playlistId) {
    this._trackIds          = tracks.map((t) => t.id ?? t._spotifyId ?? t._appleMusicId);
    this._currentPlaylistId = playlistId;
    this._queueNeedsSetup   = true;
    // pendingStartIndex is set by the caller AFTER this returns
  }

  /**
   * Switch the active music service bridge at runtime.
   * Pauses the old bridge, detaches its events, resets state,
   * then attaches the new bridge.
   */
  switchBridge(newBridge) {
    // Pause whatever is currently playing
    this.mk.pause().catch(() => {});

    // Detach old bridge events
    this._detachBridgeEvents();

    // Switch
    this.mk = newBridge;

    // Reset coordination state
    this._currentPlaylistId     = null;
    this._trackIds              = [];
    this._queueNeedsSetup       = false;
    this.pendingStartIndex      = null;
    this._pendingSkip           = null;
    this._mkAutoAdvanced        = false;
    this._webampInitiatedTarget = null;
    this._prevTrackIndex        = -1;

    // Re-attach events for the new bridge
    if (this._audioEl && this._setTime) {
      this._bridgeBridgeToAudio(this._audioEl);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Step 1 — Find & patch webamp.media._source._audio
  // ─────────────────────────────────────────────────────────────

  _findAndPatchAudio() {
    const tryPatch = () => {
      const audio = this.webamp.media?._source?._audio;
      if (audio && audio !== this._audioEl) {
        this._audioEl = audio;
        this._patchAudioElement(audio);
        console.log('[WebampController] audio patched ✓');
      }
    };

    tryPatch();

    if (!this._audioEl) {
      const timer = setInterval(() => {
        tryPatch();
        if (this._audioEl) clearInterval(timer);
      }, 50);
    }
  }

  _patchAudioElement(audio) {
    const ctrl = this;  // Use ctrl.mk for all bridge access — survives switchBridge()

    audio.muted  = true;
    audio.volume = 0;
    audio.loop   = true;

    // ── currentTime ────────────────────────────────────────────
    let _time = 0;
    // Store as instance method so switchBridge() can pass it to re-bridging
    this._setTime = (t) => { _time = t; };

    Object.defineProperty(audio, 'currentTime', {
      get: () => _time,
      set: (secs) => {
        _time = secs;
        if (secs === 0 && ctrl._mkAutoAdvanced) return;
        ctrl.mk.seekTo(secs);
      },
      configurable: true,
    });

    Object.defineProperty(audio, 'duration', {
      get: () => ctrl.mk.duration || 0,
      configurable: true,
    });

    Object.defineProperty(audio, 'paused', {
      get: () => !ctrl.mk.isPlaying,
      configurable: true,
    });

    // ── src — fire fake load events so Webamp's UI stays enabled ─
    let _src = audio.src || '';
    Object.defineProperty(audio, 'src', {
      get: () => _src,
      set: (value) => {
        _src = value;
        Promise.resolve().then(() => {
          ['loadstart', 'loadedmetadata', 'durationchange', 'canplay', 'canplaythrough']
            .forEach(evt => audio.dispatchEvent(new Event(evt)));
        });
      },
      configurable: true,
    });

    // ── play() ─────────────────────────────────────────────────
    let _playInFlight = false;

    audio.play = async () => {
      if (_playInFlight) return undefined;
      _playInFlight = true;
      ctrl._mkAutoAdvanced = false;

      try {
        const PlaybackStates = ctrl.mk.PlaybackStates;
        const state = ctrl.mk.playbackState;

        if (ctrl._pendingSkip) {
          // ── Sequential next / prev ─────────────────────────────
          const dir        = ctrl._pendingSkip;
          const curIndex   = ctrl.mk.nowPlayingItemIndex ?? 0;
          ctrl._pendingSkip = null;
          ctrl._webampInitiatedTarget = dir === 'next'
            ? curIndex + 1
            : Math.max(0, curIndex - 1);

          if (dir === 'next') {
            await ctrl.mk.skipToNextItem().catch(err => {
              console.warn('[WebampController] skipToNext:', err?.message);
              ctrl._webampInitiatedTarget = null;
            });
          } else {
            await ctrl.mk.skipToPreviousItem().catch(err => {
              console.warn('[WebampController] skipToPrev:', err?.message);
              ctrl._webampInitiatedTarget = null;
            });
          }

        } else if (ctrl._currentPlaylistId && (ctrl._queueNeedsSetup || ctrl.pendingStartIndex !== null)) {
          // ── Initial load or arbitrary jump ─────────────────────
          await ctrl.mk.stop();

          const startWith        = ctrl.pendingStartIndex ?? 0;
          ctrl._queueNeedsSetup  = false;
          ctrl.pendingStartIndex = null;
          ctrl._webampInitiatedTarget = startWith;

          await ctrl.mk.setQueue(ctrl._currentPlaylistId, startWith);

          // Wait briefly for the bridge to settle, then play if needed
          await new Promise(r => setTimeout(r, 300));

          const stateAfter = ctrl.mk.playbackState;
          const P = ctrl.mk.PlaybackStates;
          if (
            stateAfter !== P.playing &&
            stateAfter !== P.loading &&
            stateAfter !== P.waiting
          ) {
            await ctrl.mk.play().catch(err =>
              console.warn('[WebampController] play after setQueue:', err?.message)
            );
          }

        } else if (state === PlaybackStates?.playing) {
          // Already playing — don't interrupt

        } else {
          await ctrl.mk.play().catch(err =>
            console.warn('[WebampController] resume play:', err?.message)
          );
        }

      } catch (err) {
        console.warn('[WebampController] play() error:', err?.message ?? err);
        ctrl._webampInitiatedTarget = null;
      } finally {
        _playInFlight = false;
      }

      return undefined;
    };

    audio.pause = () => { ctrl.mk.pause(); };
    audio.load  = () => {};

    this._bridgeBridgeToAudio(audio);
  }

  // ─────────────────────────────────────────────────────────────
  // Bridge → synthetic audio element events
  // ─────────────────────────────────────────────────────────────

  _bridgeBridgeToAudio(audio) {
    const ctrl = this;
    const setTime = this._setTime;

    const onTimeUpdate = (e) => {
      setTime(e.detail?.currentTime ?? ctrl.mk.currentTime ?? 0);
      audio.dispatchEvent(new Event('timeupdate'));
    };

    const onPlayState = (e) => {
      const PlaybackStates = ctrl.mk.PlaybackStates;
      const state = e.detail?.state ?? ctrl.mk.playbackState;

      if (e.detail?.isPlaying) {
        audio.dispatchEvent(new Event('playing'));
      }

      // Only handle 'completed' (queue exhausted) — not per-track 'ended'.
      // Per-track advances are handled by trackchange to avoid double-skipping.
      if (state === PlaybackStates?.completed) {
        audio.loop = false;
        audio.dispatchEvent(new Event('ended'));
        audio.loop = true;
        ctrl._prevTrackIndex = -1;
      }
    };

    const onTrackChange = (item) => {
      const newIndex = ctrl.mk.nowPlayingItemIndex ?? -1;

      if (ctrl._prevTrackIndex >= 0 && newIndex !== ctrl._prevTrackIndex) {

        if (ctrl._webampInitiatedTarget !== null) {
          // We initiated this navigation — suppress auto-advance logic.
          if (newIndex === ctrl._webampInitiatedTarget) {
            console.log(`[WebampController] trackchange: reached target ${newIndex} ✓`);
            ctrl._webampInitiatedTarget = null;
          } else {
            console.log(`[WebampController] trackchange: in-flight ${ctrl._prevTrackIndex}→${newIndex}`);
          }
        } else {
          // Genuine bridge auto-advance — tell Webamp to move its cursor forward.
          console.log(`[WebampController] trackchange: auto-advance ${ctrl._prevTrackIndex}→${newIndex}`);
          ctrl._mkAutoAdvanced = true;
          audio.loop = false;
          audio.dispatchEvent(new Event('ended'));
          audio.loop = true;
        }
      }

      ctrl._prevTrackIndex = newIndex;
      setTime(0);
      audio.dispatchEvent(new Event('durationchange'));
      ctrl._syncPlaylistHighlight(item?.detail);
    };

    // Store handlers for removal on switchBridge()
    this._bridgeHandlers = {
      bridge:      this.mk,
      timeupdate:  onTimeUpdate,
      playstate:   onPlayState,
      trackchange: onTrackChange,
    };

    this.mk.addEventListener('timeupdate',  onTimeUpdate);
    this.mk.addEventListener('playstate',   onPlayState);
    this.mk.addEventListener('trackchange', onTrackChange);
  }

  _detachBridgeEvents() {
    const h = this._bridgeHandlers;
    if (!h) return;
    h.bridge.removeEventListener('timeupdate',  h.timeupdate);
    h.bridge.removeEventListener('playstate',   h.playstate);
    h.bridge.removeEventListener('trackchange', h.trackchange);
    this._bridgeHandlers = null;
  }

  // ─────────────────────────────────────────────────────────────
  // Step 2 — Volume: Redux → active bridge
  // ─────────────────────────────────────────────────────────────

  _subscribeStoreVolume() {
    const store = this.webamp.store;
    if (!store) {
      console.warn('[WebampController] webamp.store not found — volume sync disabled.');
      return;
    }

    let _lastVol = -1;
    this._unsubscribeStore = store.subscribe(() => {
      const vol = store.getState()?.media?.volume ?? null;
      if (vol === null || vol === _lastVol) return;
      _lastVol = vol;
      this.mk.setVolume(vol / 100);
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Step 3 — Redux currentTrack changes → bridge navigation
  // ─────────────────────────────────────────────────────────────

  _subscribeCurrentTrackChanges() {
    const store = this.webamp.store;
    if (!store) {
      console.warn('[WebampController] webamp.store not found — track-change sync disabled.');
      return;
    }

    let _lastTrackId = null;
    let _lastIndex   = -1;

    this._unsubscribeTrack = store.subscribe(() => {
      const state = store.getState();
      const { currentTrack, trackOrder } = state?.playlist ?? {};

      if (currentTrack == null || !trackOrder) return;
      if (currentTrack === _lastTrackId) return;
      _lastTrackId = currentTrack;

      const newIndex = trackOrder.indexOf(currentTrack);
      if (newIndex < 0) { _lastIndex = -1; return; }

      const prevIndex = _lastIndex;
      _lastIndex = newIndex;

      if (this._queueNeedsSetup) return;
      if (this._mkAutoAdvanced) return;
      if (!this._currentPlaylistId) return;

      const delta = prevIndex >= 0 ? newIndex - prevIndex : null;
      console.log(`[WebampController] user nav: ${prevIndex}→${newIndex} (delta=${delta})`);

      if (delta === 1) {
        this._pendingSkip      = 'next';
        this.pendingStartIndex = null;
      } else if (delta === -1) {
        this._pendingSkip      = 'prev';
        this.pendingStartIndex = null;
      } else {
        this.pendingStartIndex = newIndex;
        this._pendingSkip      = null;
      }
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────

  _syncPlaylistHighlight(item) {
    if (!item) return;
    const title = item.attributes?.name ?? '';
    if (!title) return;

    document.querySelectorAll('.track-cell.webamp-active-track')
      .forEach(el => el.classList.remove('webamp-active-track'));
    document.querySelectorAll('.track-cell')
      .forEach(row => {
        if (row.textContent.includes(title)) {
          row.classList.add('webamp-active-track');
          row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      });
  }

  destroy() {
    this._detachBridgeEvents();
    this._unsubscribeStore?.();
    this._unsubscribeTrack?.();
  }
}
